// http — the one way the rebuild talks to the API.
//
// Same token the classic app uses (localStorage 'token', Bearer), same base
// (/api), so both apps share a login. Deliberately fetch, not axios: the Field
// app has to tell "the server refused" (an answer) apart from "no signal" (no
// answer), and only the first should ever be shown as an error to a person.
//
//   request(path, { method, body, signal })  → parsed JSON
//   throws HttpError { status, body }        for any non-2xx answer
//   throws NetworkError                      when no answer came back at all
//
// A 401 is handed to the caller like any other refusal — the classic app's
// axios interceptor redirects on 401 in its own tab; here the shell decides.

/** The body field a person can be shown, or null. A non-string is not one. */
function sentenceOf(body) {
  const sent = body && (body.error || body.message);
  return typeof sent === 'string' && sent.trim() ? sent : null;
}

export class HttpError extends Error {
  constructor(status, body, path) {
    // Only a STRING is a sentence. `super(someObject)` stringifies, and a
    // handler answering `{ error: { field: 'title' } }` then puts the literal
    // text "[object Object]" in front of a crew member — found by a test for
    // serverSentence on 2026-09-03, which could reject the non-string itself
    // but then fell back to this message.
    super(sentenceOf(body) || `Request failed (${status})`);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
    this.path = path;
  }
}

export class NetworkError extends Error {
  constructor(path, cause) {
    super('No connection');
    this.name = 'NetworkError';
    this.path = path;
    this.cause = cause;
  }
}

export const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function authHeaders(extra = {}) {
  let token = null;
  try { token = localStorage.getItem('token'); } catch (_) { /* private mode */ }
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
}

export async function request(path, { method = 'GET', body, signal, headers } = {}) {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const init = {
    method,
    headers: authHeaders(isForm ? headers : { 'Content-Type': 'application/json', ...(headers || {}) }),
    signal,
  };
  if (body !== undefined) init.body = isForm ? body : JSON.stringify(body);

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch (e) {
    throw new NetworkError(path, e);
  }
  let parsed = null;
  const text = await res.text();
  if (text) { try { parsed = JSON.parse(text); } catch (_) { parsed = { raw: text }; } }
  if (!res.ok) throw new HttpError(res.status, parsed, path);
  return parsed;
}

export const get = (path, opts) => request(path, { ...opts, method: 'GET' });
export const post = (path, body, opts) => request(path, { ...opts, method: 'POST', body });
export const put = (path, body, opts) => request(path, { ...opts, method: 'PUT', body });
export const patch = (path, body, opts) => request(path, { ...opts, method: 'PATCH', body });
export const del = (path, opts) => request(path, { ...opts, method: 'DELETE' });

/** Is this error "no answer" rather than "the answer was no"? */
export const isOffline = (err) => err instanceof NetworkError || (err && err.name === 'NetworkError');

/**
 * Gateway statuses that mean THE APP IS NOT THERE, as opposed to the app
 * saying no. nginx answers these while node is stopped.
 */
export const GATEWAY_DOWN = Object.freeze([502, 503, 504]);

/**
 * "Nothing handled this request" — no answer at all, OR the gateway saying
 * there was nothing behind it to answer. Safe to keep and replay.
 *
 * ── Why this exists, and why it is not just `isOffline` ────────────────────
 *
 * `isOffline` is name-based: fetch threw, no answer came back. That is the
 * right test for the WORD "offline" and it is deliberately narrow — see
 * describeError below, which was corrected once for over-claiming.
 *
 * It is the wrong test for "may I keep this and send it later", and a deploy
 * is where the difference costs a crew member money. `deploy.sh` stops PM2
 * (line 142) and does not start it again until line 929 — install, rsync, npm
 * install, client build and an nginx reload all happen in between. nginx is
 * never stopped, only reloaded. So for the whole run the phone HAS signal, the
 * gateway is up, and there is nothing behind it: every request answers 502.
 *
 * A 502 is an answer, so it is an HttpError, so `isOffline` said false, so
 * every write queue in Field let it through as a hard failure. A crew member
 * who tapped clock-out during a deploy got an error and had to tap again
 * afterwards — and the second tap carries the LATER time, because the server
 * timestamps from `op.at` (routes/field.js applyOp). That is a deploy taking
 * minutes off somebody's paid day, quietly, and it is what this predicate
 * fixes.
 *
 * ── What is deliberately NOT in here ──────────────────────────────────────
 *
 *   4xx  a refusal. "Job not found" does not become true by waiting; queuing
 *        it would hold the op forever and then drop it into a banner.
 *   500  the app is UP and threw. The request may have half-happened, and
 *        retrying forever would hide a real bug behind a queue. Only "there
 *        was nothing there to handle it" is safe to replay blindly.
 *
 * Replaying is safe for the three that ARE here because every op carries a
 * `key` the server treats as its identity (REBUILD_API.md §1: "a replayed key
 * returns the original result"), so even a 504 whose request did reach node
 * cannot double-book anyone.
 */
export const isUnreachable = (err) => isOffline(err)
  || !!(err && GATEWAY_DOWN.includes(err.status));

/**
 * What to tell a person when the server refused.
 *
 * The server's own sentence whenever it sent one — "Only managers and admins
 * can move subtasks", "week already approved", a dependency message — because
 * those are written for people and name the actual obstacle. `describeError`
 * otherwise.
 *
 * `describeError` flattens every 403 to "You do not have permission to do
 * that.", which is true and useless; the server's own body says which door
 * was shut ("Admin only", "Access denied", the self-approval and dependency
 * messages).
 *
 * This is the one to reach for on a refusal a person can act on, and the
 * reason it lives here rather than in one app's lib: `describeError` flattens
 * every 403 to "You do not have permission to do that.", which is right when
 * the server said nothing and a loss when it did. Lived in
 * apps/dispatch/lib/format.js until 2026-09-03, where a shared panel could
 * not use it without importing from an application.
 */
export function serverSentence(err, fallback) {
  const sent = err && sentenceOf(err.body);
  if (sent) return sent;
  // No sentence came back. 401 and 403 have good ones of their own; for any
  // other answered status prefer the CALLER's words over HttpError's
  // synthesized "Request failed (400)", which is a status code in front of a
  // crew member — the thing this module exists to prevent. That message stays
  // on the error for diagnostics (download.test.js pins it for a non-JSON 502
  // from nginx), it just is not what a person is shown.
  const speaksForItself = err && (err.status === 401 || err.status === 403);
  if (fallback && err && err.status && !speaksForItself) return fallback;
  return describeError(err, fallback);
}

/**
 * The words used when the caller gave no fallback. Exported so the offline
 * branch below can tell "the caller said nothing" from "the caller said
 * something worth repeating" without a second parameter.
 */
export const DEFAULT_FALLBACK = 'Something went wrong';

/**
 * A short sentence for a person, never a stack trace.
 *
 * ── The offline branch, and why it stopped promising anything ───────────────
 *
 * It used to return "No connection. Your work is saved on this phone." on
 * every NetworkError, everywhere. That sentence describes Field's write queue
 * (apps/field/offline/queue.js) and it was FALSE at every call site that
 * reached it — including, measured, all of Field's own.
 *
 * Field's writes never arrive here offline: `act` (field/actions.js) queues
 * and returns { sent: false } without throwing, so nothing is caught and
 * nothing is described. The two writes that deliberately do NOT queue —
 * claimJob and setJobStatus — throw an HttpError(0) carrying their own
 * sentence, which `isOffline` (name-based) correctly does not treat as
 * offline. What DID reach this line was a failed LOAD (today.error, job.error,
 * hours.error) and every write on every other screen: moving a job on the
 * Schedule, creating a job, saving your own details, and creating an INVOICE.
 * None of those queue. Each told the person their work was safe when it was
 * gone, and on InvoiceFromJobs that is a bill nobody sent.
 *
 * So the offline case now says the true thing and hands back the caller's own
 * words for what did not happen: "No connection. Could not create the
 * invoice." The reassuring sentence still exists where it is true — the
 * offline banner (apps/shell/offline.js) shows it from the queue's own pending
 * count, which is evidence rather than an assumption.
 */
export function describeError(err, fallback = DEFAULT_FALLBACK) {
  if (!err) return fallback;
  if (isOffline(err)) {
    return fallback === DEFAULT_FALLBACK ? 'No connection.' : `No connection. ${fallback}`;
  }
  if (err instanceof HttpError || err.status) {
    if (err.status === 401) return 'Your session expired. Sign in again.';
    if (err.status === 403) return 'You do not have permission to do that.';
    return err.message || fallback;
  }
  return err.message || fallback;
}
